import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import {
  BtnBold,
  BtnItalic,
  createButton,
  Editor,
  EditorProvider,
  Toolbar,
  BtnBulletList,
  BtnClearFormatting,
  BtnLink,
  BtnNumberedList,
  BtnRedo,
  BtnUndo,
  BtnUnderline,
  BtnStrikeThrough,
  BtnStyles,
  HtmlButton,
  Separator,
} from "react-simple-wysiwyg";

/**
 * @function TextEditor
 * @description WYSIWYG editor with cursor-aware variable insertion
 * @returns JSX for TextEditor
 */
const TextEditor = forwardRef(function TextEditor({ value, onChange }, ref) {
  const BtnAlignCenter = createButton("Align center", "≡", "justifyCenter");
  const styles = { minHeight: "60vh", backgroundColor: "white" };
  const wrapperRef = useRef(null);
  const savedRangeRef = useRef(null);

  const getEditableEl = () => {
    const root = wrapperRef.current;
    if (!root) return null;
    return (
      root.querySelector("textarea.rsw-html") ||
      root.querySelector(".rsw-ce[contenteditable='true']") ||
      root.querySelector(".rsw-ce")
    );
  };

  const isInsideEditor = (node) => {
    const el = getEditableEl();
    return Boolean(el && node && el.contains(node));
  };

  const saveSelection = () => {
    const el = getEditableEl();
    if (el instanceof HTMLTextAreaElement) {
      savedRangeRef.current = {
        type: "textarea",
        start: el.selectionStart,
        end: el.selectionEnd,
      };
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (!isInsideEditor(range.commonAncestorContainer)) return;

    savedRangeRef.current = {
      type: "range",
      range: range.cloneRange(),
    };
  };

  const hasActiveEditorCursor = () => {
    const el = getEditableEl();
    if (!el) return false;

    if (el instanceof HTMLTextAreaElement) {
      return document.activeElement === el;
    }

    return (
      el === document.activeElement || el.contains(document.activeElement)
    );
  };

  const notifyChange = (nextValue) => {
    onChange?.({
      target: { value: nextValue },
    });
  };

  const insertAtCursor = (text) => {
    const el = getEditableEl();
    if (!el || !hasActiveEditorCursor()) {
      return false;
    }

    if (el instanceof HTMLTextAreaElement) {
      const saved =
        savedRangeRef.current?.type === "textarea"
          ? savedRangeRef.current
          : null;
      const start = saved?.start ?? el.selectionStart ?? el.value.length;
      const end = saved?.end ?? el.selectionEnd ?? start;
      const next = `${el.value.slice(0, start)}${text}${el.value.slice(end)}`;
      const caret = start + text.length;

      el.focus();
      el.value = next;
      el.setSelectionRange(caret, caret);
      savedRangeRef.current = { type: "textarea", start: caret, end: caret };
      notifyChange(next);
      return true;
    }

    el.focus();

    const selection = window.getSelection();
    try {
      if (savedRangeRef.current?.type === "range" && selection) {
        selection.removeAllRanges();
        selection.addRange(savedRangeRef.current.range);
      }
    } catch {
      // Range can go stale after editor rerenders
    }

    const canInsertInPlace =
      selection &&
      selection.rangeCount > 0 &&
      isInsideEditor(selection.anchorNode);

    if (!canInsertInPlace) {
      return false;
    }

    const inserted = document.execCommand("insertText", false, text);
    if (!inserted) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const node = document.createTextNode(text);
      range.insertNode(node);
      range.setStartAfter(node);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      notifyChange(el.innerHTML);
    }

    saveSelection();
    return true;
  };

  useImperativeHandle(ref, () => ({
    insertAtCursor,
    focus: () => getEditableEl()?.focus(),
  }));

  return (
    <div ref={wrapperRef}>
      <EditorProvider>
        <Editor
          value={value}
          onChange={onChange}
          onSelect={saveSelection}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          onBlur={saveSelection}
          style={styles}
        >
          <Toolbar>
            <BtnRedo />
            <Separator />
            <BtnUndo />
            <Separator />
            <BtnBold />
            <Separator />
            <BtnItalic />
            <Separator />
            <BtnUnderline />
            <Separator />
            <BtnLink />
            <Separator />
            <BtnAlignCenter />
            <Separator />
            <BtnStrikeThrough />
            <Separator />
            <BtnNumberedList />
            <Separator />
            <BtnBulletList />
            <Separator />
            <BtnClearFormatting />
            <Separator />
            <BtnStyles />
            <Separator />
            <HtmlButton />
            <Separator />
          </Toolbar>
        </Editor>
      </EditorProvider>
    </div>
  );
});

export default TextEditor;
